import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

const TimelineStep = ({ idx, title, body }) => (
  <li className="relative pl-12 pb-8 last:pb-0">
    <span className="absolute left-0 top-0 w-9 h-9 rounded-full bg-[#D63031] text-white font-display font-bold flex items-center justify-center">
      {idx}
    </span>
    <span className="absolute left-[17px] top-9 bottom-0 w-px bg-[#F39C12]/40" />
    <h4 className="font-display font-semibold text-[#2C3E50]">{title}</h4>
    <p className="text-sm text-[#2C3E50]/85 mt-1 leading-relaxed">{body}</p>
  </li>
);

const Returns = () => (
  <div className="font-body">
    <section className="bg-[#FBF7F0] py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-sm uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-3">
          Returns & Refunds
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold">
          Hassle-Free Returns & Refunds.
        </h1>
        <p className="mt-5 text-lg text-[#2C3E50]/85 max-w-2xl mx-auto">
          Your satisfaction is our priority. We make returns simple.
        </p>
      </div>
    </section>

    <section className="py-14">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-5">
        {[
          { k: "Timeline", v: "7 days from delivery" },
          { k: "Condition", v: "Unused, with original packaging" },
          { k: "Refund window", v: "5-7 business days after inspection" },
        ].map((q) => (
          <div
            key={q.k}
            className="bg-white border border-[#F39C12]/20 rounded-2xl p-6"
          >
            <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold">
              {q.k}
            </p>
            <p className="mt-2 font-display text-lg font-semibold text-[#2C3E50]">
              {q.v}
            </p>
          </div>
        ))}
      </div>
    </section>

    <section className="py-14 bg-[#FFF5E1]/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl font-bold text-[#2C3E50] mb-8">
          How to return — step by step.
        </h2>
        <ol className="space-y-0">
          <TimelineStep
            idx={1}
            title="Contact us within 7 days of delivery"
            body="Email support@gruham.in or call +91 9664527720 with your order number and reason for return."
          />
          <TimelineStep
            idx={2}
            title="We share return instructions"
            body="We'll provide a return shipping label or arrange pickup. Pack the item in its original packaging."
          />
          <TimelineStep
            idx={3}
            title="Ship back to us"
            body="Use the label or our pickup. Keep the tracking number safe — share it with us if needed."
          />
          <TimelineStep
            idx={4}
            title="We inspect (3-5 business days)"
            body="We check condition and completeness against your order."
          />
          <TimelineStep
            idx={5}
            title="Refund initiated"
            body="Once approved, refund is initiated to your original payment method."
          />
          <TimelineStep
            idx={6}
            title="Money arrives"
            body="5-7 business days depending on your bank."
          />
        </ol>
      </div>
    </section>

    <section className="py-14">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-[#F39C12]/20 rounded-2xl p-6">
          <h3 className="font-display text-xl font-semibold text-[#2C3E50] mb-4">
            Eligible for return
          </h3>
          <ul className="space-y-2 text-sm text-[#2C3E50]/85">
            {[
              "Unused, undamaged products",
              "Original packaging with all tags",
              "Defective or damaged-in-transit items (with photos)",
              "Wrong item received",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#27AE60] mt-1" />
                {x}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-[#F39C12]/20 rounded-2xl p-6">
          <h3 className="font-display text-xl font-semibold text-[#2C3E50] mb-4">
            Not eligible
          </h3>
          <ul className="space-y-2 text-sm text-[#2C3E50]/85">
            {[
              "Used / damaged products",
              "Items without original tags",
              "Personalized items",
              "Items returned after 7 days",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <span className="w-4 h-4 mt-1 rounded-full bg-[#D63031]/15 text-[#D63031] flex items-center justify-center text-[10px]">
                  ✕
                </span>
                {x}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="py-14 bg-[#D63031] text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold">
          Need help with a return?
        </h2>
        <p className="mt-4 text-white/90">
          Reach us on WhatsApp or email — usually a reply within 24 hours.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            data-testid="returns-whatsapp"
            href="https://wa.me/919664527720?text=Hi%2C%20I%20need%20help%20with%20a%20return"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-gold rounded-full h-12 px-7 font-semibold inline-flex items-center justify-center"
          >
            Chat on WhatsApp
          </a>
          <Link
            to="/track-order"
            className="rounded-full h-12 px-7 font-semibold inline-flex items-center justify-center border-2 border-white text-white hover:bg-white/10 transition"
          >
            Track an Order
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Returns;
